import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { fmesh } from "../../../core/globalVariables.js";
import {
  useGlobalRefs,
  useGlobalFileStates,
  useGlobalState
} from "../../../core/globalVariables.js";
import { TbWindowMaximize } from "react-icons/tb";
import { HiSearch } from "react-icons/hi";

const PieChart = ({
  geojson,
  isViewingPieChart,
  selectedMetric,
  handleLandtypePTagClick,
  piechartRef,
  handleEnlargePieButtonClick,
  selectedYearEditing,
  polygonCustomLayerRef,
  landKeySelectedKey,
  setLandKeySelectedKey,
  selectedKey,
  handleLandtypePieHover,
  PDFpiechartRef
}) => {

  const [searchTerm, setSearchTerm] = useState("");

  const [timeSeriesArray, setTimeSeriesArray] = useState([]);
  const [hoveredEntry, setHoveredEntry] = useState(null);
  const timeSeriesArrayRef = useRef([]);
  const extendedpiechartRef = useRef(null);

  useEffect(() => {
    timeSeriesArrayRef.current = timeSeriesArray;
  }, [timeSeriesArray]);

  const calculatePercentages = (geojson, property) => {
    const sumTotal = {};
    let grandTotal = 0;

    geojson.features.forEach((feature) => {
      const propertyValue = feature.feature_data_simple_timeseries[property]?.[selectedYearEditing];
      const metric = feature.feature_data_simple_timeseries[selectedMetric]?.[selectedYearEditing];

      if (propertyValue && typeof propertyValue === "string" && metric !== undefined) {
        const roundedMetric = Math.round(metric);
        sumTotal[propertyValue] = (sumTotal[propertyValue] || 0) + roundedMetric;
        grandTotal += roundedMetric;
      }
    });

    const percentages = {};
    for (const landKey in sumTotal) {
      percentages[landKey] = ((sumTotal[landKey] / grandTotal) * 100).toFixed(2);
    }

    return percentages;
  };

  useEffect(() => {
    const tempArray = [];
    fmesh.traverse((child) => {
      if (child.isMesh) {
        geojson.features.forEach((feature) => {
          if (child.name === feature.skylark_id) {
            const timeSeriesData = feature.feature_data_simple_timeseries[landKeySelectedKey]?.[selectedYearEditing];
            if (timeSeriesData) {
              const r = Math.round(child.material.color.r * 255);
              const g = Math.round(child.material.color.g * 255);
              const b = Math.round(child.material.color.b * 255);
              const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
              const uniqueEntry = { timeSeriesData, color: hexColor };

              if (!tempArray.some((entry) => entry.timeSeriesData === uniqueEntry.timeSeriesData && entry.color === uniqueEntry.color)) {
                tempArray.push(uniqueEntry);
              }
            }
          }
        });
      }
    });

    setTimeSeriesArray(tempArray);

    const ctx = document.getElementById("summary")?.getContext("2d");
    // if (extendedpiechartRef.current) {
    //   extendedpiechartRef.current.destroy();
    // }

    if (geojson && landKeySelectedKey) {
      const percentages = calculatePercentages(geojson, landKeySelectedKey);
      const labels = Object.keys(percentages);
      const dataValues = Object.values(percentages);

      const differentiatorColorMap = {};
      tempArray.forEach((entry) => {
        differentiatorColorMap[entry.timeSeriesData] = entry.color;
      });

      const backgroundColors = labels.map((label) => differentiatorColorMap[label] || "#ccc");

      extendedpiechartRef.current = new Chart(ctx, {
        type: "pie",
        data: {
          labels: labels,
          datasets: [
            {
              data: dataValues,
              backgroundColor: backgroundColors,
              borderWidth: 0,
              borderColor: "transparent",
              hoverOffset: 0,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false, // disable default tooltip
              external: (context) => {
                // Tooltip element
                let tooltipEl = document.getElementById("chartjs-fixed-tooltip");

                if (!tooltipEl) {
                  tooltipEl = document.createElement("div"); // create the element first
                  tooltipEl.id = "chartjs-fixed-tooltip";

                  tooltipEl.style.position = "absolute";
                  tooltipEl.style.top = "120px";
                  tooltipEl.style.left = "290px";
                  tooltipEl.style.color = "white";
                  tooltipEl.style.padding = "6px 8px";
                  tooltipEl.style.borderRadius = "6px";
                  tooltipEl.style.pointerEvents = "none";
                  tooltipEl.style.transition = "all .1s ease";
                  tooltipEl.style.whiteSpace = "nowrap";
                  tooltipEl.style.display = "inline-block";

                  context.chart.canvas.parentNode.appendChild(tooltipEl);
                }

                const tooltipModel = context.tooltip;

                if (tooltipModel.opacity === 0) {
                  tooltipEl.style.opacity = 0;
                  return;
                }

                if (tooltipModel.dataPoints && tooltipModel.dataPoints.length) {
                  const dataPoint = tooltipModel.dataPoints[0];
                  const dataset = context.chart.data.datasets[dataPoint.datasetIndex];
                  const segmentColor = dataset.backgroundColor[dataPoint.dataIndex];

                  // Set tooltip background to segment color
                  tooltipEl.style.background = segmentColor;

                  // Set label + value
                  const label = dataPoint.label; // segment label
                  const value = dataset.data[dataPoint.dataIndex]; // segment value
                  const labelAndValueForDisplay = label + " " + value
                  tooltipEl.innerHTML = labelAndValueForDisplay;

                  tooltipEl.style.width = "auto"
                }

                tooltipEl.style.opacity = 1;
              },
            },
          },
          animation: false,
          onHover: (event, chartElement) => {
            const chart = extendedpiechartRef.current;
            if (!chart) return;

            const dataset = chart.data.datasets[0];

            if (chartElement.length) {
              const activeIndex = chartElement[0].index;

              // Highlight active slice, grey out others
              dataset.backgroundColor = dataset.backgroundColor.map((color, i) =>
                i === activeIndex ? timeSeriesArray[i].color : greyOutColor(timeSeriesArray[i].color, 0.9)
              );

              // Set active element for tooltip
              chart.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);
              chart.tooltip.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);

              // Call external hover handler
              const label = chart.data.labels[activeIndex];
              handleLandtypePieHover(label, landKeySelectedKey);
            } else {
              // Reset colors if no slice hovered
              dataset.backgroundColor = timeSeriesArray.map((entry) => entry.color);
              chart.setActiveElements([]);
              chart.tooltip.setActiveElements([]);
              handleLandtypePieHover("Show all");
            }

            chart.update();
          }
        },
      });


    }

    return () => {
      if (extendedpiechartRef.current) {
        extendedpiechartRef.current.destroy();
      }
    };
  }, [geojson, landKeySelectedKey, selectedMetric, selectedYearEditing, piechartRef, extendedpiechartRef, timeSeriesArray.length]);

  const handleMouseEnter = (landtype) => {
    hoverPieSegment(landtype);
    handleLandtypePieHover(landtype, landKeySelectedKey);
    setHoveredEntry(landtype);
  };

  const handleMouseLeave = () => {
    clearHover();
    handleLandtypePieHover("Show all");
    setHoveredEntry(null);
  };

  useEffect(() => {
    if (piechartRef.current) {
      console.log("Pie chart DOM ref is set:", piechartRef.current);
    }
  }, [piechartRef]);

  const hoverPieSegment = (label) => {
    const chart = extendedpiechartRef.current;
    if (!chart || !timeSeriesArrayRef.current.length) return;

    const dataset = chart.data.datasets[0];
    const activeIndex = chart.data.labels.indexOf(label);
    if (activeIndex === -1) return;

    // Update background colors using the ref
    dataset.backgroundColor = chart.data.labels.map((lbl, i) => {
      const entryColor = timeSeriesArrayRef.current[i]?.color || "#ccc";
      return i === activeIndex ? entryColor : greyOutColor(entryColor, 0.9);
    });

    // Set the active element
    chart.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);

    // Update tooltip manually
    chart.tooltip.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);
    chart.update();
  };

  // Convert hex color to desaturated grey-ish version
  const greyOutColor = (hex, factor = 0.5) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Convert to grey using luminosity formula, then mix with original
    const grey = Math.round(0.3 * r + 0.59 * g + 0.11 * b);
    const newR = Math.round(r * (1 - factor) + grey * factor);
    const newG = Math.round(g * (1 - factor) + grey * factor);
    const newB = Math.round(b * (1 - factor) + grey * factor);

    return `rgb(${newR},${newG},${newB})`;
  };

  const clearHover = () => {
    const chart = extendedpiechartRef.current;
    if (!chart || !timeSeriesArrayRef.current.length) return;

    const dataset = chart.data.datasets[0];

    // Reset to original colors safely
    dataset.backgroundColor = chart.data.labels.map((_, i) => {
      return timeSeriesArrayRef.current[i]?.color || "#ccc";
    });

    chart.setActiveElements([]);
    chart.tooltip.setActiveElements([]);
    chart.update();
  };

  const filteredEntries = [...timeSeriesArray]
    .filter(entry =>
      entry.timeSeriesData.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aNum = /^\d/.test(a.timeSeriesData);
      const bNum = /^\d/.test(b.timeSeriesData);
      if (aNum && !bNum) return -1;
      if (!aNum && bNum) return 1;
      return a.timeSeriesData.localeCompare(b.timeSeriesData, undefined, { numeric: true, sensitivity: "base" });
    });

  return (
    <div style={{height: '100%'}}>
      {geojson && (
        <div ref={piechartRef} style={{height: '100%'}}>
          <canvas
            id="summary"
            className="pieChart"
            onMouseLeave={handleMouseLeave}
          />
          <div className="enlargePieButton" onClick={handleEnlargePieButtonClick}>
            <TbWindowMaximize />
          </div>

          {/* Search Bar */}
          <div style={{ marginTop: "20px", paddingRight: "30px" }}>
            <div style={{position: 'absolute', fontSize: '22px', right: '10px', marginTop: '12.5px', color: '#4C5758'}}><HiSearch /></div>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "15px",
                borderRadius: "3px",
                border: "0px solid #ccc",
                fontSize: "14px",
                backgroundColor: '#F2F1F6'
              }}
            />
          </div>

          {/* List */}
          <div
            style={{
              marginTop: "20px",
              overflowY: "scroll",
              height: "100%",
            }}
          >
            {filteredEntries.map((entry) => (
              <div
                key={entry.timeSeriesData}
                style={{
                  paddingTop: "15px",
                  paddingLeft: "15px",
                  paddingBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "3px",
                  backgroundColor:
                    hoveredEntry === entry.timeSeriesData
                      ? "#F2F1F6"
                      : "transparent",
                }}
                onMouseEnter={() => handleMouseEnter(entry.timeSeriesData)}
                onMouseLeave={handleMouseLeave}
              >
                <span
                  style={{
                    width: "15px",
                    height: "15px",
                    borderRadius: "50%",
                    backgroundColor: entry.color,
                    display: "inline-block",
                    marginRight: "10px",
                  }}
                ></span>
                {entry.timeSeriesData}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PieChart;
